import { Injectable, inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';

import { BehaviorSubject, filter, first, firstValueFrom } from 'rxjs';

import * as Sentry from '@sentry/angular';

import { Store } from '@ngrx/store';
import {
  selectCountries,
  selectCurrencies,
  selectSalutations,
} from 'src/store/core-data/core-data.state';
import { selectEnterprises } from 'src/store/enterprises/enterprises.state';
import { selectPartnerEnterprise } from 'src/store/partner-enterprise/partner-enterprise.state';
import {
  selectPermissions,
  selectSettings,
  selectUser,
  selectUserId,
} from 'src/store/user/user.state';
import {
  setCountries,
  setCurrencies,
  setSalutations,
} from 'src/store/core-data/core-data.actions';
import {
  setPermissions,
  setSettings,
  setUser,
} from 'src/store/user/user.actions';
import { setPartnerEnterprise } from 'src/store/partner-enterprise/partner-enterprise.actions';
import { setEnterprises } from 'src/store/enterprises/enterprises.actions';
import { selectIsAppInitialized } from 'src/store/app/app.state';

import { CoreDataService } from 'src/services/core-data.service';
import { EnterpriseService } from 'src/services/enterprise.service';
import { UserService } from 'src/services/user.service';
import { ToastService } from 'lipp-components';
import { TranslationService } from 'lipp-components';

import { Utility } from 'src/services/utility.service';
import { Logger } from 'src/services/logger.service';

import { Countries } from 'src/models/Countries';
import { Currencies } from 'src/models/Currencies';
import { Enterprise } from 'src/models/Enterprise';
import { Permissions } from 'src/models/Permissions';
import { Salutations } from 'src/models/Salutations';
import { Settings } from 'src/models/Settings';
import { User } from 'src/models/User';

import { EnUserType } from 'src/enums/EnUserType';

export const PagesGuard: CanActivateFn = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Promise<boolean | UrlTree> => {
  const router = inject(Router);
  const store = inject(Store);
  const loadCrutialDataService = inject(LoadCrutailDataService);

  await firstValueFrom(
    store
      .select(selectIsAppInitialized)
      .pipe(filter((isAppInitialized) => isAppInitialized === true))
  );

  if (!Utility.hasAuthToken) {
    Logger.logError(`ERROR PagesGuard: User not logged in.`);
    return router.createUrlTree(['/auth/login']);
  }

  loadCrutialDataService.loadCrutialData();

  if (
    (await firstValueFrom(
      successfullyLoadedCountries$.pipe(filter((value) => value !== undefined))
    )) === false
  ) {
    return router.createUrlTree(['/error']);
  } else if (
    (await firstValueFrom(
      successfullyLoadedCurrencies$.pipe(filter((value) => value !== undefined))
    )) === false
  ) {
    return router.createUrlTree(['/error']);
  } else if (
    (await firstValueFrom(
      successfullyLoadedSalutations$.pipe(
        filter((value) => value !== undefined)
      )
    )) === false
  ) {
    return router.createUrlTree(['/error']);
  } else if (
    (await firstValueFrom(
      successfullyLoadedUser$.pipe(filter((value) => value !== undefined))
    )) === false
  ) {
    return router.createUrlTree(['/error']);
  } else if (
    (await firstValueFrom(
      successfullyLoadedSettings$.pipe(filter((value) => value !== undefined))
    )) === false
  ) {
    return router.createUrlTree(['/error']);
  } else if (
    (await firstValueFrom(
      successfullyLoadedPermissions$.pipe(
        filter((value) => value !== undefined)
      )
    )) === false
  ) {
    return router.createUrlTree(['/error']);
  } else if (
    (await firstValueFrom(
      successfullyLoadedEnterprises$.pipe(
        filter((value) => value !== undefined)
      )
    )) === false
  ) {
    return router.createUrlTree(['/error']);
  } else if (
    (await firstValueFrom(
      successfullyLoadedPartnerEnterprise$.pipe(
        filter((value) => value !== undefined)
      )
    )) === false
  ) {
    return router.createUrlTree(['/error']);
  }

  const user = (await firstValueFrom(store.select(selectUser)))!;
  const isQuicklinkUser: boolean = user?.type === EnUserType.QuickLink;
  if (
    isQuicklinkUser &&
    !(
      state.url.includes('/table-reservation/list') ||
      state.url.includes('/table-reservation/overview')
    )
  ) {
    Logger.logError(
      `ERROR PagesGuard: Quick link user is not allowed to visit this page.`
    );
    return router.createUrlTree(['/auth/login']);
  }

  return true;
};

const successfullyLoadedCountries$ = new BehaviorSubject<boolean | undefined>(
  undefined
);
const successfullyLoadedCurrencies$ = new BehaviorSubject<boolean | undefined>(
  undefined
);
const successfullyLoadedSalutations$ = new BehaviorSubject<boolean | undefined>(
  undefined
);

const successfullyLoadedUser$ = new BehaviorSubject<boolean | undefined>(
  undefined
);
const successfullyLoadedSettings$ = new BehaviorSubject<boolean | undefined>(
  undefined
);
const successfullyLoadedPermissions$ = new BehaviorSubject<boolean | undefined>(
  undefined
);

const successfullyLoadedEnterprises$ = new BehaviorSubject<boolean | undefined>(
  undefined
);
const successfullyLoadedPartnerEnterprise$ = new BehaviorSubject<
  boolean | undefined
>(undefined);

@Injectable({ providedIn: 'root' })
class LoadCrutailDataService {
  constructor(
    private store: Store,
    private coreDataService: CoreDataService,
    private userService: UserService,
    private enterpriseService: EnterpriseService,
    private toastService: ToastService,
    private translationService: TranslationService
  ) {}

  public async loadCrutialData(): Promise<void> {
    successfullyLoadedCountries$.next(undefined);
    successfullyLoadedCurrencies$.next(undefined);
    successfullyLoadedSalutations$.next(undefined);
    successfullyLoadedUser$.next(undefined);
    successfullyLoadedSettings$.next(undefined);
    successfullyLoadedPermissions$.next(undefined);
    successfullyLoadedEnterprises$.next(undefined);
    successfullyLoadedPartnerEnterprise$.next(undefined);

    if (
      (await firstValueFrom(this.store.select(selectCountries))) === undefined
    ) {
      this.coreDataService.getCountries().subscribe((countriesResult) => {
        if (countriesResult.ok) {
          const countries: Countries = countriesResult.value!;
          this.store.dispatch(setCountries({ countries }));

          successfullyLoadedCountries$.next(true);
        } else {
          this.toastService.showAlert(
            this.translationService.translate('FatalError.LoadingCountries'),
            this.translationService.translate(countriesResult.errors![0].key)
          );

          successfullyLoadedCountries$.next(false);
        }
      });
    } else {
      successfullyLoadedCountries$.next(true);
    }

    if (
      (await firstValueFrom(this.store.select(selectCurrencies))) === undefined
    ) {
      this.coreDataService.getCurrencies().subscribe((currenciesResult) => {
        if (currenciesResult.ok) {
          const currencies: Currencies = currenciesResult.value!;
          this.store.dispatch(setCurrencies({ currencies }));

          successfullyLoadedCurrencies$.next(true);
        } else {
          this.toastService.showAlert(
            this.translationService.translate('FatalError.LoadingCurrencies'),
            this.translationService.translate(currenciesResult.errors![0].key)
          );

          successfullyLoadedCurrencies$.next(false);
        }
      });
    } else {
      successfullyLoadedCurrencies$.next(true);
    }

    if (
      (await firstValueFrom(this.store.select(selectSalutations))) === undefined
    ) {
      this.coreDataService.getSalutations().subscribe((salutationsResult) => {
        if (salutationsResult.ok) {
          const salutations: Salutations = salutationsResult.value!;
          this.store.dispatch(setSalutations({ salutations }));

          successfullyLoadedSalutations$.next(true);
        } else {
          this.toastService.showAlert(
            this.translationService.translate('FatalError.LoadingSalutations'),
            this.translationService.translate(salutationsResult.errors![0].key)
          );

          successfullyLoadedSalutations$.next(false);
        }
      });
    } else {
      successfullyLoadedSalutations$.next(true);
    }

    if (
      (await firstValueFrom(this.store.select(selectSettings))) === undefined
    ) {
      const userId: number = (await firstValueFrom(
        this.store.select(selectUserId)
      ))!;

      this.userService.getSettings(userId).subscribe((settingsResult) => {
        if (settingsResult.ok) {
          const settings: Settings = settingsResult.value!;
          this.store.dispatch(setSettings({ settings }));

          successfullyLoadedSettings$.next(true);
        } else {
          this.toastService.showAlert(
            this.translationService.translate('FatalError.LoadingSettings'),
            this.translationService.translate(settingsResult.errors![0].key)
          );

          successfullyLoadedSettings$.next(false);
        }
      });
    } else {
      successfullyLoadedSettings$.next(true);
    }

    if ((await firstValueFrom(this.store.select(selectUser))) === undefined) {
      const userId: number = (await firstValueFrom(
        this.store.select(selectUserId)
      ))!;

      this.userService.getUser(userId).subscribe((userResult) => {
        if (userResult.ok) {
          const user: User = userResult.value!;
          this.store.dispatch(setUser({ user }));

          successfullyLoadedUser$.next(true);
        } else {
          this.toastService.showAlert(
            this.translationService.translate('FatalError.LoadingUser'),
            this.translationService.translate(userResult.errors![0].key)
          );

          successfullyLoadedUser$.next(false);
        }
      });
    } else {
      const user = (await firstValueFrom(this.store.select(selectUser)))!;

      Sentry.setUser({
        id: user.id,
        username: `${user.firstName} ${user.lastName}`.trim(),
      });

      //#region Disabling Replays for User Sebastian Linecker (because otherwise random replays without errors are being recorded somehow)
      const sebastianLineckerUserId = 8751;
      const isReplayOptOutUser = user.id === sebastianLineckerUserId;

      if (isReplayOptOutUser) {
        try {
          Sentry.getReplay()?.stop();
        } catch {}
      }
      //#endregion

      successfullyLoadedUser$.next(true);
    }

    const user = (await firstValueFrom(
      this.store.select(selectUser).pipe(
        filter((user) => user !== undefined),
        first()
      )
    ))!;

    if (!user.isLineckerEmployee && !user.isLineckerPartner) {
      if (
        (await firstValueFrom(this.store.select(selectPermissions))) ===
        undefined
      ) {
        this.userService.getPermissions().subscribe((permissionsResult) => {
          if (permissionsResult.ok) {
            const permissions: Permissions = permissionsResult.value!;
            this.store.dispatch(setPermissions({ permissions }));

            successfullyLoadedPermissions$.next(true);
          } else {
            this.toastService.showAlert(
              this.translationService.translate(
                'FatalError.LoadingPermissions'
              ),
              this.translationService.translate(
                permissionsResult.errors![0].key
              )
            );

            successfullyLoadedPermissions$.next(false);
          }
        });
      } else {
        successfullyLoadedPermissions$.next(true);
      }
    } else {
      successfullyLoadedPermissions$.next(true);
    }

    if (user.isLineckerEmployee || user.isLineckerPartner) {
      if (
        (await firstValueFrom(this.store.select(selectPartnerEnterprise))) ===
        undefined
      ) {
        this.enterpriseService
          .getPartnerEnterpriseForCurrentUser()
          .subscribe((partnerEnterpriseResult) => {
            if (partnerEnterpriseResult.ok) {
              const partnerEnterprise: Enterprise =
                partnerEnterpriseResult.value!;
              this.store.dispatch(setPartnerEnterprise({ partnerEnterprise }));

              successfullyLoadedPartnerEnterprise$.next(true);
            } else {
              this.toastService.showAlert(
                this.translationService.translate(
                  'FatalError.LoadingPartnerEnterprise'
                ),
                this.translationService.translate(
                  partnerEnterpriseResult.errors![0].key
                )
              );

              successfullyLoadedPartnerEnterprise$.next(false);
            }
          });
      } else {
        successfullyLoadedPartnerEnterprise$.next(true);
      }
    } else {
      successfullyLoadedPartnerEnterprise$.next(true);
    }

    if (
      (await firstValueFrom(this.store.select(selectEnterprises))) === undefined
    ) {
      if (!user.isLineckerEmployee && !user.isLineckerPartner) {
        this.enterpriseService
          .getEnterprisesForCurrentUser()
          .subscribe((enterprisesResult) => {
            if (enterprisesResult.ok) {
              const enterprises: Enterprise[] = enterprisesResult.value!;

              this.store.dispatch(setEnterprises({ enterprises }));

              successfullyLoadedEnterprises$.next(true);
            } else {
              this.toastService.showAlert(
                this.translationService.translate(
                  'FatalError.LoadingEnterprises'
                ),
                this.translationService.translate(
                  enterprisesResult.errors![0].key
                )
              );

              successfullyLoadedEnterprises$.next(false);
            }
          });
      } else {
        const selectedEnterpriseId = (await firstValueFrom(
          this.store.select(selectSettings).pipe(
            filter((settings) => settings !== undefined),
            first()
          )
        ))!.selectedEnterpriseId;

        if (selectedEnterpriseId !== undefined) {
          this.enterpriseService
            .getEnterpriseForLippPortal(selectedEnterpriseId)
            .subscribe((enterpriseResult) => {
              if (enterpriseResult.ok) {
                if (enterpriseResult.value) {
                  const enterprise: Enterprise = enterpriseResult.value;

                  this.store.dispatch(
                    setEnterprises({ enterprises: [enterprise] })
                  );
                } else {
                  this.store.dispatch(setEnterprises({ enterprises: [] }));
                }

                successfullyLoadedEnterprises$.next(true);
              } else {
                this.toastService.showAlert(
                  this.translationService.translate(
                    'FatalError.LoadingSelectedEnterprise'
                  ),
                  this.translationService.translate(
                    enterpriseResult.errors![0].key
                  )
                );

                successfullyLoadedEnterprises$.next(false);
              }
            });
        } else {
          this.store.dispatch(setEnterprises({ enterprises: [] }));

          successfullyLoadedEnterprises$.next(true);
        }
      }
    } else {
      successfullyLoadedEnterprises$.next(true);
    }
  }
}
